import { Toaster } from '@arckit/daisyui/blocks-client';
import { createNoopReporter } from '@arckit/telemetry/error-reporter';
import { RouterProvider } from '@tanstack/react-router';
import { type FC, useMemo } from 'react';
import { EVENT_TRACKER_CONFIG } from '@/configuration/telemetry/event-tracker';
import { NAVBAR_CONFIG } from '@/features/brand/injection';
import { MAP_CONFIG } from '@/features/cartographie/injection';
import { ContactAction } from '@/features/contact';
import { provide } from '@/libraries/injection';

import { invalidateMapLocationIfChanged, THEME_COLORS } from '@/libraries/map';
import { API_BASE_URL } from '@/shared/injection/keys/api-base-url.key';
import { CONTACT_ACTION } from '@/shared/injection/keys/contact-action.key';
import { ERROR_REPORTER } from '@/shared/injection/keys/error-reporter.key';
import { SITE_URL } from '@/shared/injection/keys/site-url.key';
import { TERRITOIRE_FILTER, type TerritoireType } from '@/shared/injection/keys/territoire-filter.key';
import { getThemeColors } from '@/shared/ui';
import { createAppRouter } from './router';

type AppProps = {
  apiUrl?: string;
  siteUrl?: string;
  logoUrl?: string;
  appName?: string;
  helpUrl?: string;
  helpLabel?: string;
  latitude?: number;
  longitude?: number;
  zoom?: number;
  territoireType?: string;
  territoires?: string;
  routeInitiale?: string;
  matomoUrl?: string;
  matomoSiteId?: string;
};

export const App: FC<AppProps> = ({
  apiUrl = '',
  siteUrl,
  logoUrl,
  appName,
  helpUrl,
  helpLabel,
  latitude,
  longitude,
  zoom,
  territoireType,
  territoires,
  routeInitiale,
  matomoUrl,
  matomoSiteId
}) => {
  provide(API_BASE_URL, apiUrl);
  provide(ERROR_REPORTER, createNoopReporter());
  provide(SITE_URL, siteUrl);
  provide(NAVBAR_CONFIG, { logoUrl, appName, helpUrl, helpLabel, homeUrl: routeInitiale });
  provide(MAP_CONFIG, { latitude, longitude, zoom });
  invalidateMapLocationIfChanged({ latitude, longitude, zoom });
  provide(TERRITOIRE_FILTER, {
    type: territoireType as TerritoireType | undefined,
    codes: territoires
      ?.split(',')
      .map((s) => s.trim())
      .filter(Boolean)
  });
  provide(EVENT_TRACKER_CONFIG, { url: matomoUrl, siteId: matomoSiteId });
  provide(THEME_COLORS, getThemeColors);
  provide(CONTACT_ACTION, ContactAction);

  const router = useMemo(() => createAppRouter(routeInitiale), [routeInitiale]);

  return (
    <>
      <Toaster directionY='toast-top' directionX='toast-center' />
      <RouterProvider router={router} />
    </>
  );
};
